import { Card } from '../../../db/types'
import { CardsFrontUnion, Settings, useSettingsStore } from '../../../stores/settings'

export const isCardKnown = (deltaX: number) => deltaX > 0

const SWIPE_ACTIVATION_DISTANCE = 20

export const isCardWithinLimit = (deltaX: number) => Math.abs(deltaX) <= SWIPE_ACTIVATION_DISTANCE

export const isVerticalSwipeDominant = (deltaX: number, deltaY: number) => Math.abs(deltaY) > Math.abs(deltaX)

export const isCardSnoozedBySwipe = (deltaX: number, deltaY: number) =>
  isVerticalSwipeDominant(deltaX, deltaY) && deltaY > SWIPE_ACTIVATION_DISTANCE

const DAY_IN_MS = 24 * 60 * 60 * 1000

type SnoozeSettings = Pick<Settings, 'snoozeDelayDays' | 'smartSnoozeEnabled'>

export type CardSnoozeFields = Pick<
  Card,
  'snoozeCountSource' | 'snoozedUntilSource' | 'snoozeCountLearn' | 'snoozedUntilLearn'
>

const getSmartSnoozeMultiplier = (snoozeCount: number) => 2 ** Math.max(snoozeCount - 1, 0)

export const getSnoozeDelayDaysWithSettings = (
  snoozeCount: number,
  { snoozeDelayDays, smartSnoozeEnabled }: SnoozeSettings,
) => {
  if (!smartSnoozeEnabled || !snoozeCount) return snoozeDelayDays

  return snoozeDelayDays * getSmartSnoozeMultiplier(snoozeCount)
}

export const getSnoozeDelayDays = (snoozeCount: number) => {
  const { snoozeDelayDays, smartSnoozeEnabled } = useSettingsStore.getState()

  return getSnoozeDelayDaysWithSettings(snoozeCount, { snoozeDelayDays, smartSnoozeEnabled })
}

const getSnoozeSlotKeys = (cardsFront: CardsFrontUnion) => {
  const isLearnFront = cardsFront === 'learn'

  return {
    countKey: isLearnFront ? 'snoozeCountLearn' : 'snoozeCountSource',
    untilKey: isLearnFront ? 'snoozedUntilLearn' : 'snoozedUntilSource',
  } as const
}

export const getActiveSnoozeCount = (card: CardSnoozeFields, cardsFront: CardsFrontUnion) =>
  card[getSnoozeSlotKeys(cardsFront).countKey]

export const isCardWasSnoozedForFront = (
  card: CardSnoozeFields,
  cardsFront: CardsFrontUnion = useSettingsStore.getState().cardsFront,
) => !!card[getSnoozeSlotKeys(cardsFront).countKey]

export const isCardSnoozedForFront = (
  card: CardSnoozeFields,
  cardsFront: CardsFrontUnion = useSettingsStore.getState().cardsFront,
) => {
  const snoozedUntil = card[getSnoozeSlotKeys(cardsFront).untilKey]

  return !!snoozedUntil && Date.now() < snoozedUntil
}

export const canPlayCard = (
  card: CardSnoozeFields,
  cardsFront: CardsFrontUnion = useSettingsStore.getState().cardsFront,
) => !isCardSnoozedForFront(card, cardsFront)

export const getPlayableCardsSnoozeSelector = (cardsFront: CardsFrontUnion, now = Date.now()) => {
  if (cardsFront === 'learn') return { $or: [{ snoozeCountLearn: 0 }, { snoozedUntilLearn: { $lt: now } }] }

  return { $or: [{ snoozeCountSource: 0 }, { snoozedUntilSource: { $lt: now } }] }
}

export const getSnoozeUntil = (snoozeCount: number) => Date.now() + getSnoozeDelayDays(snoozeCount) * DAY_IN_MS

export const buildSnoozePatch = (snoozeCount: number) => {
  const { cardsFront, scopeSnoozeToFrontLanguage } = useSettingsStore.getState()
  const snoozedUntil = getSnoozeUntil(snoozeCount)

  if (!scopeSnoozeToFrontLanguage)
    return {
      snoozeCountSource: snoozeCount,
      snoozedUntilSource: snoozedUntil,
      snoozeCountLearn: snoozeCount,
      snoozedUntilLearn: snoozedUntil,
    }

  const { countKey, untilKey } = getSnoozeSlotKeys(cardsFront)

  return { [countKey]: snoozeCount, [untilKey]: snoozedUntil }
}

export const buildClearSnoozePatch = () => {
  const { cardsFront, scopeSnoozeToFrontLanguage } = useSettingsStore.getState()

  if (!scopeSnoozeToFrontLanguage)
    return {
      snoozeCountSource: 0,
      snoozedUntilSource: undefined,
      snoozeCountLearn: 0,
      snoozedUntilLearn: undefined,
    }

  const { countKey, untilKey } = getSnoozeSlotKeys(cardsFront)

  return { [countKey]: 0, [untilKey]: undefined }
}

export const incrementSnooze = (card: CardSnoozeFields) => {
  const { cardsFront, scopeSnoozeToFrontLanguage } = useSettingsStore.getState()
  const currentCount = scopeSnoozeToFrontLanguage
    ? getActiveSnoozeCount(card, cardsFront)
    : Math.max(card.snoozeCountSource, card.snoozeCountLearn)

  return buildSnoozePatch(currentCount + 1)
}

//! pick utility doesn't work with RxDocument
export const extractSnoozeProps = (card: CardSnoozeFields): CardSnoozeFields => ({
  snoozeCountSource: card.snoozeCountSource,
  snoozedUntilSource: card.snoozedUntilSource,
  snoozeCountLearn: card.snoozeCountLearn,
  snoozedUntilLearn: card.snoozedUntilLearn,
})
